import { useEffect, useState } from 'react';
import { Save, X } from 'lucide-react';
import { Apirequests } from '../../utils/ApiRequests';
import { ErrorTost, SuccessTost } from '../../utils/ToastHelper';
import BtnSpinner from '../../components/BtnSpinner';

function AddOrEditClass({ close, setRefresh, currentClass }) {
    const token = localStorage.getItem("token");

    const [data, setData] = useState({
        title: "",
        dateText: "",
        language: "",
        priceText: "",
        price: "",
        timeText: "",
        time: "",
        joinBtn: false,
        date: "",
        pageLink: "",
        btnText: "",
        status: "",
        dateFormat: "",
        notes:{currency: ""}
    });

    const [isEdit, setIsEdit] = useState(false);
    const [previewImage, setPreviewImage] = useState("");
    const [backgroundImage, setBackgroundImage] = useState("");
    const [loading, setLoading] = useState({ state: false, name: "" });
    const [imageFile, setImageFile] = useState(null);
    const [backgroundImageFile, setBackgroundImageFile] = useState(null);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Handle checkbox inputs
        if (type === 'checkbox') {
            setData(prev => ({
                ...prev,
                [name]: checked
            }));
            return;
        }

        // Handle file inputs separately - don't add them to the data state
        if (type === 'file') {
            const file = e.target.files[0];
            if (file) {
                if (name === 'classImage') {
                    setImageFile(file);
                    const url = URL.createObjectURL(file);
                    setPreviewImage(url);
                } else if (name === 'backgroundImage') {
                    setBackgroundImageFile(file);
                    const url = URL.createObjectURL(file);
                    setBackgroundImage(url);
                }
            }
            return;
        }

        // Handle all other inputs
        setData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const addImage = async (file) => {
        if (!file) return { success: false, message: "No file selected" };

        const formData = new FormData();
        formData.append('image', file);

        try {
            const response = await fetch(process.env.REACT_APP_SERVER_URI + "image/upload-image", {
                method: "POST",
                body: formData
            });
            const data = await response.json();
            return data;
        } catch (error) {
            return { success: false, message: "Error uploading image" };
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading({ state: true, name: currentClass ? "editclass" : "addclass" });

        let formData = { ...data };

        // Handle image uploads
        if (imageFile) {
            const addClassImage = await addImage(imageFile);
            if (addClassImage?.success) {
                formData.image = addClassImage?.data;
            } else {
                ErrorTost(addClassImage?.message || "Failed to upload class image");
                setLoading({ state: false, name: "" });
                return;
            }
        } else if (currentClass?.image) {
            formData.image = currentClass.image;
        }

        if (backgroundImageFile) {
            const addBackgroundImage = await addImage(backgroundImageFile);
            if (addBackgroundImage?.success) {
                formData.backgroundImage = addBackgroundImage?.data;
            } else {
                ErrorTost(addBackgroundImage?.message || "Failed to upload background image");
                setLoading({ state: false, name: "" });
                return;
            }
        } else if (currentClass?.backgroundImage) {
            formData.backgroundImage = currentClass.backgroundImage;
        }

        // Now submit with the complete data
        if (currentClass) {
            await handleEditClass(currentClass?._id, formData);
        } else {
            await handleAddClass(formData);
        }
    };

    const handleAddClass = async (formData) => {
        try {
            const res = await Apirequests("classes/create-class", "POST", formData, {
                Authorization: `Bearer ${token}`,
            });
            if (res?.success) {
                SuccessTost(res?.message);
                close();
                setRefresh((prev) => !prev);
            } else {
                ErrorTost(res?.message || "Failed to create class");
            }
        } catch (error) {
            console.log(error, 'create class error');
            ErrorTost("Something went wrong, please try again later.");
        }
        setLoading({ state: false, name: "" });
    };

    const handleEditClass = async (id, formData) => {
        try {
            const res = await Apirequests(`classes/edit-class/${id}`, 'PUT', formData, {
                Authorization: `Bearer ${token}`
            });
            if (res?.success) {
                SuccessTost(res?.message);
                close();
                setRefresh((prev) => !prev);
            } else {
                ErrorTost(res?.message || "Failed to update class");
            }
        } catch (error) {
            console.log(error, 'edit class error');
            ErrorTost("Something went wrong, please try again later.");
        }
        setLoading({ state: false, name: "" });
    };

    const handleReset = () => {
        setData({
            title: "",
            dateText: "",
            language: "",
            priceText: "",
            price: "",
            timeText: "",
            time: "",
            joinBtn: false,
            date: "",
            pageLink: "",
            btnText: "",
            status: "",
        });
        setPreviewImage("");
        setBackgroundImage("");
        setImageFile(null);
        setBackgroundImageFile(null);
        setIsEdit(false);
    };


    useEffect(() => {
        if (currentClass) {
            // console.log(currentClass?.date)
            const formattedDate = new Date(currentClass?.date).toISOString().split("T")[0]

            setData({
                title: currentClass?.title || "",
                dateText: currentClass?.dateText || "",
                date: formattedDate || "",
                timeText: currentClass?.timeText || "",
                time: currentClass?.time || "",
                priceText: currentClass?.priceText || "",
                price: currentClass?.price || "",
                pageLink: currentClass?.pageLink || "",
                btnText: currentClass?.btnText || "",
                joinBtn: currentClass?.joinBtn || false,
                status: currentClass?.status || "",
                language: currentClass?.language || "",
                notes: {...currentClass?.notes,currency:currentClass?.notes?.currency || ""}
            });

            if (currentClass?.backgroundImage) {
                setBackgroundImage(`${process.env.REACT_APP_IMAGE_URI}${currentClass.backgroundImage}`);
            }

            if (currentClass?.image) {
                setPreviewImage(`${process.env.REACT_APP_IMAGE_URI}${currentClass.image}`);
            }

            setIsEdit(true);
        }

        return () => {
            handleReset();
        };
    }, [currentClass]);

    return (
        <div className="py-2 px-2 sm:px-2 lg:px-2">
            <div>
                {/* Header */}
                <div className="flex justify-between items-center mb-2">
                    <h1 className="text-3xl font-bold text-gray-900">
                        {isEdit ? 'Edit Class' : 'Add New Class'}
                    </h1>
                    <div className="flex gap-4">
                        {!isEdit && <button
                            onClick={handleReset}
                            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700"
                        >
                            Reset Form
                        </button>}
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl">
                    {/* Image Preview */}
                    <div className="mb-8">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Class Image Preview
                        </label>
                        <div className="relative flex text-center h-72 w-full bg-gray-100 rounded-lg overflow-hidden">
                            <div className="w-1/2">
                                {previewImage ? (
                                    <img
                                        src={previewImage}
                                        alt="Course preview"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-gray-400">
                                        No image preview available
                                    </div>
                                )}
                            </div>

                            <div className="w-1/2">
                                {backgroundImage ? (
                                    <img
                                        src={backgroundImage}
                                        alt="Background preview"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-gray-400">
                                        No background image available
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Basic Information */}
                    <div>
                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Class Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={data.title}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter title"
                                required
                            />
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Image *
                            </label>
                            <input
                                type="file"
                                name="classImage"
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                accept="image/*"
                                required={!isEdit}
                            />
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Background Image *
                            </label>
                            <input
                                type="file"
                                name="backgroundImage"
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                accept="image/*"
                                required={!isEdit}
                            />
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date Text
                            </label>
                            <input
                                type="text"
                                name="dateText"
                                value={data.dateText}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter Date text"
                            />
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Language
                            </label>
                            <input
                                type="text"
                                name="language"
                                value={data.language}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter Language"
                            />
                        </div>
                    </div>

                    {/* Course Details */}
                    <div>
                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Price Text
                            </label>
                            <input
                                type="text"
                                name="priceText"
                                value={data.priceText}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter Price Text"
                            />
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Currency
                            </label>
                            <select
                                value={data?.notes?.currency}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                onChange={(e) => setData({ ...data, notes: { ...data?.notes, currency: e.target.value } })}>
                                <option value={""}>Select Currency</option>
                                <option value={"INR"}>Indian Rupee (INR)</option>
                                <option value={"USD"}>United States Dollar (USD)</option>
                            </select>
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Price *
                            </label>
                            <input
                                type="tel"
                                name="price"
                                value={data.price}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter price"
                                required
                            />
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Time Text
                            </label>
                            <input
                                type="text"
                                name="timeText"
                                value={data.timeText}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter Time text"
                            />
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Time *
                            </label>
                            <input
                                type="text"
                                name="time"
                                value={data.time}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter time"
                                required
                            />
                        </div>

                        <div className='mb-2 flex items-center border border-gray-200 py-2 px-4 mt-4 rounded-lg'>
                            <label className="inline-block flex-none text-sm font-medium text-gray-700 ">
                                Join Btn
                            </label>
                            <input
                                type="checkbox"
                                name="joinBtn"
                                checked={data.joinBtn}
                                onChange={handleInputChange}
                                className="ms-5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Page Link *
                            </label>
                            <input
                                type="text"
                                name="pageLink"
                                value={data.pageLink}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter Page link"
                                required
                            />
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date
                            </label>
                            <input
                                type="date"
                                name="date"
                                value={data.date}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter Date"
                            />
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Enter Date Format *
                            </label>
                            <input
                                type="text"
                                name="dateFormat"
                                value={data.dateFormat}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter Date Format"
                            />
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Button Text *
                            </label>
                            <input
                                type="text"
                                name="btnText"
                                value={data.btnText}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter Button Text"
                                required
                            />
                        </div>

                        <div className='mb-2'>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status *
                            </label>
                            <select
                                name="status"
                                value={data.status}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Select Status</option>
                                <option value="Active">Active</option>
                                <option value="Disabled">Disabled</option>
                            </select>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex gap-4">
                        {isEdit ? (
                            <button
                                type="submit"
                                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                            >
                                {
                                    loading.state && loading.name === "editclass" ?
                                        <BtnSpinner /> :
                                        <>
                                            <Save className="w-5 h-5" />
                                            <span className='text-white'>Update Class</span>
                                        </>
                                }
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                            >
                                {
                                    loading.state && loading.name === "addclass" ?
                                        <BtnSpinner /> :
                                        <>
                                            <Save className="w-5 h-5" />
                                            <span className='text-white'>Create Class</span>
                                        </>
                                }
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={close}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                        >
                            <X className="w-5 h-5" />
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AddOrEditClass;